import { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { LocateFixed } from "lucide-react";
import { musicianColor, musicianLabel, approxPos } from "./mapUtils";

const SP = { lat: -23.5613, lng: -46.6565 };
const TILE = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const reduceMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const zoomFor = (r) => (r <= 5 ? 12 : r <= 10 ? 11 : 10);
const esc = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Posição dos músicos é ilustrativa: distância aproximada real + direção fictícia (nunca a posição exata).
export default function MapPlantao({ center, musicians = [], radiusKm = 5, onPick, pickMode = false, height = 320, label = "Mapa de músicos próximos", describedBy, route, tracker, showRadius = true }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const circleRef = useRef(null);
  const pinRef = useRef(null);
  const routeRef = useRef(null);
  const trackRef = useRef(null);
  const radiusRef = useRef(radiusKm);
  const pickRef = useRef({ onPick, pickMode });
  pickRef.current = { onPick, pickMode };
  const c = center && center.lat != null ? center : SP;

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    if (!document.getElementById("exp-pulse-style")) {
      const st = document.createElement("style");
      st.id = "exp-pulse-style";
      st.textContent = "@keyframes exppulse{0%{transform:scale(0.7);opacity:0.9}100%{transform:scale(1.9);opacity:0}}.exp-pulse{animation:exppulse 1.3s ease-out infinite}.exp-pick{cursor:crosshair!important}.leaflet-marker-icon:focus-visible{outline:3px solid #FFB27A;outline-offset:3px;border-radius:50%}";
      document.head.appendChild(st);
    }
    const map = L.map(ref.current, { zoomControl: false, attributionControl: false, scrollWheelZoom: false, keyboard: true });
    L.control.zoom({ position: "bottomleft", zoomInTitle: "Aproximar", zoomOutTitle: "Afastar" }).addTo(map);
    map.setView([c.lat, c.lng], zoomFor(radiusKm));
    L.tileLayer(TILE, { maxZoom: 16 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    map.on("click", (ev) => { const p = pickRef.current; if (p.pickMode && p.onPick) p.onPick({ lat: ev.latlng.lat, lng: ev.latlng.lng }); });
    mapRef.current = map;
    const sizeT = setTimeout(() => { if (mapRef.current === map) map.invalidateSize(); }, 180);
    return () => { clearTimeout(sizeT); map.remove(); mapRef.current = null; layerRef.current = null; circleRef.current = null; pinRef.current = null; routeRef.current = null; trackRef.current = null; };
  }, []); // eslint-disable-line

  useEffect(() => { ref.current?.classList.toggle("exp-pick", !!pickMode); }, [pickMode]);

  useEffect(() => { mapRef.current?.setView([c.lat, c.lng], zoomFor(radiusKm)); }, [c.lat, c.lng]); // eslint-disable-line

  // Centro (local do evento) + raio animado
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pinRef.current) {
      pinRef.current = L.marker([c.lat, c.lng], { draggable: !!onPick, keyboard: true, title: "Local do evento", alt: "Local do evento", zIndexOffset: 1000,
        icon: L.divIcon({ className: "", iconSize: [40, 40], iconAnchor: [20, 20],
          html: `<div style="width:40px;height:40px;border-radius:50%;background:#FF4D00;display:flex;align-items:center;justify-content:center;box-shadow:0 0 0 8px rgba(255,77,0,.25);border:2px solid #fff"><svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg></div>` }) }).addTo(map);
      pinRef.current.on("dragend", (ev) => { const p = ev.target.getLatLng(); pickRef.current.onPick?.({ lat: p.lat, lng: p.lng }); });
    } else pinRef.current.setLatLng([c.lat, c.lng]);
    if (!circleRef.current) {
      circleRef.current = L.circle([c.lat, c.lng], { radius: radiusKm * 1000, color: "#FF4D00", weight: 2, opacity: showRadius ? 0.7 : 0, dashArray: "6 6", fillColor: "#FF4D00", fillOpacity: showRadius ? 0.08 : 0, interactive: false }).addTo(map);
    } else circleRef.current.setLatLng([c.lat, c.lng]);
    const from = radiusRef.current, to = radiusKm;
    radiusRef.current = to;
    if (from === to || reduceMotion()) { circleRef.current.setRadius(to * 1000); return; }
    let raf, t0;
    const step = (t) => { t0 = t0 || t; const k = Math.min((t - t0) / 900, 1); circleRef.current?.setRadius((from + (to - from) * (1 - Math.pow(1 - k, 3))) * 1000); if (k < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    map.flyTo([c.lat, c.lng], zoomFor(to), { duration: 0.9 });
    return () => cancelAnimationFrame(raf);
  }, [c.lat, c.lng, radiusKm, onPick]);

  // Músicos (anonimizados)
  useEffect(() => {
    const lg = layerRef.current;
    if (!lg) return;
    lg.clearLayers();
    musicians.filter((m) => m.distance_km != null && m.distance_km <= 20).slice(0, 40).forEach((m, i) => {
      const [lat, lng] = approxPos(c, m.distance_km, i, m.id);
      const col = musicianColor(m);
      const inside = m.status || m.distance_km <= radiusKm;
      const bg = m.photo ? `background-image:url('${esc(m.photo)}');background-size:cover;background-position:center;` : "background:#1C1C24;";
      const pulse = m.status === "aceitou" || m.confirmed ? `<div class="exp-pulse" style="position:absolute;inset:-6px;border-radius:50%;border:2px solid ${col.hex}"></div>` : "";
      const name = m.stage_name || "Músico";
      const inst = m.instrument || m.instruments?.[0] || "";
      const txt = `${name}${inst ? `, ${inst}` : ""}, cerca de ${Math.round(m.distance_km)} km, ${musicianLabel(m)}${inside ? "" : ", fora do raio atual"}`;
      L.marker([lat, lng], { keyboard: true, title: txt, alt: txt, opacity: inside ? 1 : 0.4,
        icon: L.divIcon({ className: "", iconSize: [34, 34], iconAnchor: [17, 17],
          html: `<div style="position:relative;width:34px;height:34px">${pulse}<div style="width:34px;height:34px;border-radius:50%;${bg}border:3px ${col.dash ? "dashed" : "solid"} ${col.hex};box-shadow:0 1px 6px rgba(0,0,0,.6)"></div><div aria-hidden="true" style="min-width:16px;height:16px;padding:0 3px;border-radius:8px;background:${col.hex};color:#09090B;font:800 10px/16px sans-serif;text-align:center;position:absolute;right:-5px;bottom:-4px;border:1.5px solid #09090B">${col.glyph}</div></div>` }) })
        .bindTooltip(esc(txt), { direction: "top", offset: [0, -16] }).addTo(lg);
    });
  }, [musicians, radiusKm, c.lat, c.lng]);

  // Rota real (OSRM) e posição autorizada do músico contratado
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    routeRef.current?.remove(); routeRef.current = null;
    if (route?.length > 1) {
      routeRef.current = L.polyline(route, { color: "#3B82F6", weight: 5, opacity: 0.9, interactive: false }).addTo(map);
      map.fitBounds(routeRef.current.getBounds(), { padding: [40, 40], animate: !reduceMotion() });
    }
  }, [route]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!tracker || tracker.lat == null) { trackRef.current?.remove(); trackRef.current = null; return; }
    const bg = tracker.photo ? `background-image:url('${esc(tracker.photo)}');background-size:cover;background-position:center;` : "background:#1C1C24;";
    const icon = L.divIcon({ className: "", iconSize: [44, 44], iconAnchor: [22, 22],
      html: `<div style="width:44px;height:44px;border-radius:50%;${bg}border:3px solid ${tracker.stale ? "#A1A1AA" : "#3B82F6"};box-shadow:0 0 0 6px rgba(59,130,246,.25)"></div>` });
    if (!trackRef.current) trackRef.current = L.marker([tracker.lat, tracker.lng], { icon, keyboard: true, title: tracker.label, alt: tracker.label }).addTo(map);
    else { trackRef.current.setLatLng([tracker.lat, tracker.lng]); trackRef.current.setIcon(icon); }
  }, [tracker?.lat, tracker?.lng, tracker?.stale, tracker?.photo, tracker?.label]); // eslint-disable-line

  const recenter = () => mapRef.current?.flyTo([c.lat, c.lng], zoomFor(radiusKm), { duration: reduceMotion() ? 0 : 0.6 });

  return (
    <div className="relative isolate">
      <div ref={ref} role="region" aria-label={label} aria-describedby={describedBy} data-testid="express-map"
        className="w-full rounded-2xl overflow-hidden border border-white/10 focus-visible:ring-2 focus-visible:ring-[#FF8A3D]" style={{ height, background: "#0D0D11" }} />
      <button type="button" onClick={recenter} data-testid="express-map-recenter" aria-label="Centralizar mapa no local do evento"
        className="absolute bottom-14 right-2 z-[500] h-11 w-11 rounded-full bg-black/70 backdrop-blur border border-white/15 flex items-center justify-center text-white hover:bg-black/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D]">
        <LocateFixed className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
}
